import { Button } from "@jetblue/kit/button";
import { FieldError } from "@jetblue/kit/field-error";
import { InputCheckbox } from "@jetblue/kit/input-checkbox";
import { InputRoute } from "@jb-dotcom/dotcom-components/input-route";
import { InputText } from "@jetblue/kit/input-text";
import { BestFaresSingleDatePicker } from "../components/best-fares/single-date-picker";

import type { FlightSearchFormLabels } from "../labels";
import type { StatelessFlightSearchFormProperties } from "../stateless";
import type { useFlightSearchForm } from "../hooks/use-flight-search-form";
import type { PassengerCounts } from "../use-passengers";
import type { useForm } from "../use-form";

export type OneWayProperties = {
  errors: ReturnType<typeof useFlightSearchForm>["errors"];
  formList: ReturnType<typeof useForm>;
  labels: FlightSearchFormLabels;
  odQuery: StatelessFlightSearchFormProperties["odQuery"];
  passengers: PassengerCounts;
  showDatePickerPricing: boolean;
  showPromoCode?: boolean | undefined;
};
export const OneWay = ({
  errors,
  formList: {
    endDate,
    first: { departureDate, destination, origin },
    modify,
    promoCode,
    setPromoCode,
    setUsePoints,
    startDate,
    swapOriginDestination,
    usePoints,
  },
  labels,
  odQuery,
  passengers,
  showDatePickerPricing,
  showPromoCode,
}: OneWayProperties) => (
  <div className="flex flex-col gap-1 lg:flex-row">
    <InputRoute
      className="w-full data-[show-promo-code=true]:w-1/2 lg:w-3/5"
      data-fs-element="route"
      data-show-promo-code={showPromoCode}
      destination={destination}
      destinationName="to"
      labels={labels}
      odRouteData={{ destination: odQuery.data, origin: odQuery.data }}
      onDestinationChange={(value) => {
        modify(0, "destination", value);
      }}
      onOriginChange={(value) => {
        modify(0, "origin", value);
      }}
      onSwapClick={() => {
        swapOriginDestination(0);
      }}
      origin={origin}
      originName="from"
      showValidation={errors.showValidation}
    />

    <div
      className="flex flex-col gap-1 lg:w-2/5 lg:flex-row data-[show-promo-code=true]:lg:w-1/2"
      data-show-promo-code={showPromoCode}
    >
      <div className="flex w-full flex-row gap-1" data-fs-element="date">
        <BestFaresSingleDatePicker
          className="w-full"
          destination={destination}
          endDate={endDate}
          error={
            <FieldError>
              {({ isInvalid }: { isInvalid: boolean }) =>
                isInvalid ? labels.errors.departureDate : ""
              }
            </FieldError>
          }
          labels={{
            close: labels.close,
            done: labels.done,
            input: labels.departureDate,
            reset: labels.reset,
          }}
          origin={origin}
          passengers={passengers}
          selectedDate={departureDate}
          setSelectedDate={(date) => {
            modify(0, "departureDate", date);
          }}
          showDatePickerPricing={showDatePickerPricing}
          showValidation={errors.showValidation}
          startDate={startDate}
          usePoints={usePoints}
        />
        <input
          className="hidden"
          name="depart"
          readOnly
          value={departureDate?.toString()}
        />

        {showPromoCode && (
          <InputText
            className="w-full"
            data-fs-element="promo-code"
            label={labels.promoCode}
            labelClassName="w-4/5 truncate"
            name="airPromo"
            onChange={(_, value) => {
              setPromoCode(value);
            }}
            value={promoCode}
          />
        )}
      </div>

      <div className="mt-2 flex justify-center text-legal md:hidden">
        <InputCheckbox
          data-fs-element="points-toggle"
          isSelected={usePoints}
          onChange={() => {
            setUsePoints((current) => !current);
          }}
        >
          {labels.loyaltyCheckbox}
        </InputCheckbox>
      </div>

      <div className="mt-4 flex justify-center lg:mt-0">
        <Button
          className="h-14 w-full whitespace-nowrap md:w-fit md:min-w-unset lg:rounded-l-none"
          data-fs-element="submit"
          size="large"
          type="submit"
        >
          {labels.submit}
        </Button>
      </div>
    </div>
  </div>
);
